import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart3, Loader2, UserCog, GraduationCap, ShieldAlert, Filter, X,
} from "lucide-react";

const FORMACAO_OPTS = [
  { value: "todas", label: "Todas" },
  { value: "primeiro_interventor", label: "Primeiro Interventor em Cenários de Crise" },
  { value: "negociacao", label: "C-Ext-NA — Curso de Extensão em Negociação e Análise" },
];

const fmt = (v) => (v != null ? Number(v).toFixed(2).replace(".", ",") : "—");

const Stat = ({ label, value, cls = "", testid }) => (
  <div className="rounded-lg border border-border bg-card/60 px-3 py-2 text-center" data-testid={testid}>
    <div className={`font-heading text-xl font-extrabold ${cls}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{label}</div>
  </div>
);

export default function RelatoriosInstrutores() {
  const navigate = useNavigate();
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [formacao, setFormacao] = useState("todas");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (inicio) params.set("inicio", inicio);
      if (fim) params.set("fim", fim);
      if (formacao && formacao !== "todas") params.set("formacao", formacao);
      const { data } = await api.get(`/relatorios/instrutores?${params.toString()}`);
      setData(data.instrutores || []);
    } finally { setLoading(false); }
  }, [inicio, fim, formacao]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const limpar = () => { setInicio(""); setFim(""); setFormacao("todas"); setTimeout(load, 0); };

  return (
    <div className="max-w-6xl mx-auto" data-testid="relatorios-instrutores">
      <div className="flex items-center gap-3 mb-1">
        <div className="p-2 rounded-xl bg-primary/15"><BarChart3 className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">Relatórios por Instrutor</h1>
          <p className="text-sm text-muted-foreground">Indicadores por instrutor, separados por formação (metodologias não se misturam).</p>
        </div>
      </div>

      <Card className="p-4 bg-card border-border mt-5 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px] flex-1">
            <Label>Formação</Label>
            <Select value={formacao} onValueChange={setFormacao}>
              <SelectTrigger className="h-9" data-testid="rel-formacao"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMACAO_OPTS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`rel-formacao-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Início</Label>
            <Input type="date" data-testid="rel-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)} className="h-9" />
          </div>
          <div>
            <Label>Fim</Label>
            <Input type="date" data-testid="rel-fim" value={fim} onChange={(e) => setFim(e.target.value)} className="h-9" />
          </div>
          <Button data-testid="rel-aplicar" onClick={load}><Filter className="h-4 w-4 mr-1.5" />Aplicar</Button>
          {(inicio || fim || formacao !== "todas") && <Button variant="ghost" data-testid="rel-limpar" onClick={limpar}><X className="h-4 w-4 mr-1.5" />Limpar</Button>}
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !data.length ? (
        <Card className="p-10 text-center bg-card border-border"><UserCog className="h-9 w-9 mx-auto text-muted-foreground mb-2" /><p className="text-muted-foreground text-sm">Nenhum instrutor ativo encontrado.</p></Card>
      ) : (
        <div className="space-y-4">
          {data.map((r) => (
            <Card key={r.instrutor.id} data-testid={`rel-instrutor-${r.instrutor.id}`} className="p-5 bg-card border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-primary/15"><UserCog className="h-5 w-5 text-primary" /></div>
                <div className="min-w-0">
                  <div className="font-heading text-lg font-bold truncate">{r.instrutor.nome}</div>
                  {r.instrutor.email && <div className="text-xs text-muted-foreground truncate">{r.instrutor.email}</div>}
                </div>
              </div>

              {/* Totais gerais (contagem) — sem médias combinadas */}
              <div className="flex flex-wrap gap-x-5 gap-y-1 mb-4 text-xs text-muted-foreground font-mono">
                <span>Responsável por: <b className="text-foreground">{r.totais.turmas_responsavel}</b></span>
                <span>Instrutor em: <b className="text-foreground">{r.totais.turmas_instrutor}</b></span>
                <span>Avaliações (total): <b className="text-foreground">{r.totais.avaliacoes}</b></span>
              </div>

              {/* Blocos de desempenho SEPARADOS por formação */}
              {r.formacoes.length ? (
                <div className="space-y-4 mb-2">
                  {r.formacoes.map((f) => (
                    <div key={f.contexto} data-testid={`rel-formacao-bloco-${r.instrutor.id}-${f.contexto}`}
                      className="rounded-xl border border-border bg-secondary/20 p-3">
                      <div className="flex items-center gap-2 mb-2.5">
                        {f.contexto === "primeiro_interventor"
                          ? <ShieldAlert className="h-4 w-4 text-amber-400" />
                          : <GraduationCap className="h-4 w-4 text-primary" />}
                        <span className="text-xs font-semibold uppercase tracking-wide">{f.label}</span>
                      </div>
                      <div className={`grid grid-cols-3 gap-2 ${f.contexto === "primeiro_interventor" ? "sm:grid-cols-6" : "sm:grid-cols-5"}`}>
                        <Stat label="Avaliações" value={f.total} />
                        <Stat label="Concluídas" value={f.concluidas} cls="text-emerald-400" />
                        <Stat label="Aptos" value={f.aptos} cls="text-emerald-400" />
                        <Stat label="Não aptos" value={f.nao_aptos} cls="text-red-400" />
                        <Stat label="Média (1–5)" value={fmt(f.media_geral)} cls="text-primary" testid={`rel-media-${r.instrutor.id}-${f.contexto}`} />
                        {f.contexto === "primeiro_interventor" && (
                          <Stat label="Nota (0–10)" value={fmt(f.nota_final_0a10)} cls="text-amber-400" testid={`rel-nota10-${r.instrutor.id}`} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mb-2">Sem avaliações no período/formação.</p>
              )}

              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <TurmaList titulo="Responsável por" turmas={r.turmas_responsavel} navigate={navigate} testid={`resp-${r.instrutor.id}`} />
                <TurmaList titulo="Instrutor em" turmas={r.turmas_instrutor} navigate={navigate} testid={`inst-${r.instrutor.id}`} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TurmaList({ titulo, turmas, navigate, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-1.5">{titulo} ({turmas.length})</div>
      {turmas.length ? (
        <div className="space-y-1.5">
          {turmas.map((t) => (
            <button key={t.id} onClick={() => navigate(`/turmas/${t.id}`)} data-testid={`rel-turma-${t.id}`}
              className="w-full flex items-center gap-2 text-left rounded-md border border-border bg-card/60 px-3 py-2 hover:border-primary transition-colors">
              {t.curso_tipo === "instrucao"
                ? <ShieldAlert className="h-4 w-4 text-amber-400 shrink-0" />
                : <GraduationCap className="h-4 w-4 text-primary shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate text-sm">{t.nome}</div>
                <div className="text-xs text-muted-foreground truncate">{t.curso_nome || "—"}{t.data_inicio ? ` · ${t.data_inicio}` : ""}</div>
              </div>
              <Badge className="bg-secondary text-secondary-foreground border-border text-[10px] shrink-0">{t.status}</Badge>
            </button>
          ))}
        </div>
      ) : <p className="text-xs text-muted-foreground">Nenhuma turma no período.</p>}
    </div>
  );
}
